import { Mail, MapPin, Phone, Clock, ShieldCheck, Ambulance } from "lucide-react";
import PageHeader from "@/components/site/PageHeader";
import SITE from "@/lib/site";

export default function Contact() {
  return (
    <div data-testid="contact-page">
      <PageHeader
        eyebrow="Contact"
        title="Reach the Ambuly team."
        description="For medical emergencies, please call your local emergency line immediately. For partnerships, support and general enquiries, the quickest way to reach us is by email — we reply within one business day."
      />

      <section className="py-20 sm:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10">
          {/* LEFT: Primary contact card */}
          <div className="lg:col-span-7">
            <div className="rounded-3xl border border-[#D5DDD7] bg-white p-8 sm:p-10">
              <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#1A3A32]">Get in touch</p>
              <h2 className="mt-3 text-3xl sm:text-4xl font-[Outfit] font-semibold text-[#14241F]">
                We'd love to hear from you.
              </h2>
              <p className="mt-4 text-[#495D56] text-lg">
                Whether you're a patient, a hospital, an operator or an insurance
                partner — write to us and a team member will respond personally.
              </p>

              <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-6">
                <a
                  href={`mailto:${SITE.legal.email}`}
                  data-testid="contact-email-link"
                  className="group flex items-start gap-4 rounded-2xl border border-[#D5DDD7] bg-white p-6 hover:border-[#1A3A32] hover:shadow-md transition-all"
                >
                  <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-[#EDF1ED] text-[#1A3A32] group-hover:bg-[#1A3A32] group-hover:text-white transition-colors">
                    <Mail className="h-5 w-5" />
                  </span>
                  <span>
                    <span className="block text-xs tracking-[0.2em] uppercase text-[#495D56]">Email</span>
                    <span className="block mt-1 text-lg font-semibold text-[#14241F] break-all">{SITE.legal.email}</span>
                    <span className="block mt-1 text-sm text-[#495D56]">We reply within one business day.</span>
                  </span>
                </a>

                <div data-testid="contact-address-card" className="flex items-start gap-4 rounded-2xl border border-[#D5DDD7] bg-white p-6">
                  <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-[#EDF1ED] text-[#1A3A32]">
                    <MapPin className="h-5 w-5" />
                  </span>
                  <span>
                    <span className="block text-xs tracking-[0.2em] uppercase text-[#495D56]">Registered office</span>
                    <span className="block mt-1 text-base font-medium text-[#14241F] leading-relaxed">{SITE.legal.address}</span>
                  </span>
                </div>

                <div data-testid="contact-hours-card" className="flex items-start gap-4 rounded-2xl border border-[#D5DDD7] bg-white p-6">
                  <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-[#EDF1ED] text-[#1A3A32]">
                    <Clock className="h-5 w-5" />
                  </span>
                  <span>
                    <span className="block text-xs tracking-[0.2em] uppercase text-[#495D56]">Hours</span>
                    <span className="block mt-1 text-base font-medium text-[#14241F]">Support replies: Mon – Sat, 9:00 – 20:00 IST</span>
                    <span className="block mt-1 text-sm text-[#495D56]">Emergency dispatch: 24 / 7 via local emergency line.</span>
                  </span>
                </div>

                <div data-testid="contact-partners-card" className="flex items-start gap-4 rounded-2xl border border-[#D5DDD7] bg-white p-6">
                  <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-[#EDF1ED] text-[#1A3A32]">
                    <ShieldCheck className="h-5 w-5" />
                  </span>
                  <span>
                    <span className="block text-xs tracking-[0.2em] uppercase text-[#495D56]">Partnerships</span>
                    <span className="block mt-1 text-base font-medium text-[#14241F]">Hospitals, insurers, ambulance operators &amp; corporates</span>
                    <span className="block mt-1 text-sm text-[#495D56]">Email us with “Partnership” in the subject line.</span>
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* RIGHT: Emergency card */}
          <aside className="lg:col-span-5">
            <div className="rounded-3xl bg-[#14241F] text-white p-8 sm:p-10 sticky top-24">
              <div className="flex items-center gap-3">
                <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-[#E5383B]/15 text-[#E5383B]">
                  <Ambulance className="h-5 w-5" />
                </span>
                <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#E5383B]">Medical emergency?</p>
              </div>
              <h3 className="mt-5 text-2xl sm:text-3xl font-[Outfit] font-semibold leading-tight">
                Don't wait for an email. Call the local emergency line now.
              </h3>
              <p className="mt-4 text-[#D5DDD7]/85">
                In a life-threatening situation, every second matters. Use the
                national emergency number first, then reach out to us for follow-up
                support.
              </p>

              <a
                href={`tel:${SITE.legal.emergencyLine}`}
                data-testid="contact-emergency-btn"
                className="mt-8 w-full inline-flex items-center justify-center bg-[#E5383B] text-white hover:bg-[#BA1826] active:bg-[#A4161A] rounded-lg px-6 py-4 font-bold text-lg shadow-lg shadow-red-500/20 transition-colors"
              >
                <Phone className="mr-2 h-5 w-5" /> Call {SITE.legal.emergencyLine}
              </a>

              <p className="mt-6 text-sm text-[#D5DDD7]/70">
                Ambuly is not a substitute for emergency medical services. Once
                the patient is stable, email us at{" "}
                <a href={`mailto:${SITE.legal.email}`} className="underline hover:text-white">{SITE.legal.email}</a>{" "}
                and we'll help with next steps.
              </p>
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
}
